import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight, Shield, Clock } from 'lucide-react';

const Hero = ({ lang = 'en' }) => {
  const isAr = lang === 'ar';

  const openWhatsApp = () => {
    const message = encodeURIComponent(
      isAr
        ? 'مرحباً سوبر كلاسيكي، أود حجز خدمة غسيل سيارات.'
        : 'Hi Super Classy, I want to book a car wash service.',
    );
    window.open(`https://wa.me/96550040026?text=${message}`, '_blank');
  };

  const content = {
    titleTop: isAr ? 'عناية فاخرة' : 'Luxury Car Care',
    titleBottom: isAr ? 'عند باب بيتك' : 'At Your Doorstep',
    description: isAr
      ? 'استمتع بخدمة تلميع سيارات متنقلة متميزة في الكويت مع سوبر كلاسيكي .خدمة احترافية، منتجات صديقة للبيئة، ونتائج استثنائية تصلك أينما كنت.'
      : 'Experience premium mobile car detailing in Kuwait with Super Classy. Professional service, eco-friendly products, and exceptional results delivered to your location.',
    ctaMain: isAr ? 'احجز الآن عبر واتساب' : 'Book Now on WhatsApp',
    ctaSecondary: isAr ? 'عرض الباقات' : 'View Packages',
    stats: [
      { value: '50000+', label: isAr ? 'سيارة مخدومة' : 'Cars Washed' },
      { value: '4.9★', label: isAr ? 'تقييم' : 'Rating' },
      { value: '45min', label: isAr ? 'متوسط الوقت' : 'Avg. Time' },
    ],
    floating: {
      safe: isAr ? 'آمن ١٠٠٪' : '100% Safe',
      time: isAr ? '٤٥-٦٠ دقيقة' : '45-60 min',
    },
  };

  return (
    <section
      className='relative min-h-screen flex items-center justify-center overflow-hidden bg-black text-white'
      dir={isAr ? 'rtl' : 'ltr'}
    >
      {/* Background Effects */}
      <div className='absolute inset-0 bg-black'>
        <div className='absolute top-1/4 left-1/4 w-96 h-96 bg-purple-700/20 rounded-full blur-3xl animate-pulse' />
        <div className='absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000' />
      </div>

      <div className='relative section-padding py-32'>
        <div className='max-w-7xl mx-auto'>
          <div className='grid lg:grid-cols-2 gap-12 items-center'>
            {/* Content Section */}
            <motion.div
              initial={{ opacity: 0, x: isAr ? 50 : -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className={isAr ? 'text-right' : 'text-left'}
            >
              <h1 className='text-5xl md:text-7xl font-black mb-6 leading-tight'>
                <span className='text-purple-400'>{content.titleTop}</span>
                <br />
                <span className='text-white'>{content.titleBottom}</span>
              </h1>

              <p className='text-xl text-gray-300 mb-8 max-w-2xl leading-relaxed'>
                {content.description}
              </p>

              <div className='flex flex-col sm:flex-row gap-4 mb-12'>
                <button
                  onClick={openWhatsApp}
                  className='group flex items-center justify-center gap-3 px-8 py-4 bg-purple-600 hover:bg-purple-700 text-white rounded-full font-bold text-lg hover:shadow-2xl transition-all duration-300'
                >
                  {content.ctaMain}
                  <ArrowRight
                    className={`h-5 w-5 transition-transform ${
                      isAr
                        ? 'rotate-180 group-hover:-translate-x-2'
                        : 'group-hover:translate-x-2'
                    }`}
                  />
                </button>

                <a
                  href='#packages'
                  className='px-8 py-4 bg-white/10 backdrop-blur-md rounded-full font-bold text-center hover:bg-white/20 transition-all border border-white/20'
                >
                  {content.ctaSecondary}
                </a>
              </div>

              {/* Stats */}
              <div className='grid grid-cols-3 gap-6 max-w-md'>
                {content.stats.map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    className='text-center border-r border-gray-700 last:border-0'
                  >
                    <div className='text-2xl font-black text-purple-400'>
                      {stat.value}
                    </div>
                    <div className='text-xs font-bold text-gray-400 uppercase tracking-tighter'>
                      {stat.label}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Image Section */}
            <motion.div
              initial={{ opacity: 0, x: isAr ? -50 : 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className='relative'
            >
              <div className='bg-white/10 backdrop-blur-md p-2 rounded-[2.5rem] shadow-2xl'>
                <img
                  src='/assets/images/hero/hero-showcase.jpg'
                  alt='Premium Detailing'
                  className='w-full h-96 md:h-[550px] object-cover rounded-[2rem]'
                />
              </div>

              {/* Floating Elements */}
              <motion.div
                animate={{ y: [0, -20, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className={`absolute -top-4 ${
                  isAr ? '-left-4' : '-right-4'
                } bg-white/10 backdrop-blur-md p-4 rounded-2xl shadow-xl border border-white/20`}
              >
                <div className='flex items-center gap-2'>
                  <Shield className='h-6 w-6 text-green-400' />
                  <span className='font-bold'>{content.floating.safe}</span>
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [0, -15, 0] }}
                transition={{ duration: 4, repeat: Infinity, delay: 0.5 }}
                className={`absolute -bottom-4 ${
                  isAr ? '-right-4' : '-left-4'
                } bg-white/10 backdrop-blur-md p-4 rounded-2xl shadow-xl border border-white/20`}
              >
                <div className='flex items-center gap-2'>
                  <Clock className='h-6 w-6 text-blue-400' />
                  <span className='font-bold'>{content.floating.time}</span>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className='absolute bottom-8 left-1/2 -translate-x-1/2'
      >
        <div className='w-px h-12 bg-gradient-to-b from-purple-500 to-transparent' />
      </motion.div>
    </section>
  );
};

export default Hero;

